import { BooleanInput } from '@angular/cdk/coercion';
import { booleanAttribute, Component, computed, inject, input, model, signal } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { LucideCheck, LucideChevronDown, LucideChevronUp, LucideDynamicIcon } from '@lucide/angular';
import { NgpSelect, NgpSelectDropdown, NgpSelectOption, NgpSelectPortal } from 'ng-primitives/select';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';
import { InlineButtonComponent } from '../../atoms/inline-button/inline-button.component';
import { SelectPortalContainerService } from '../../utils/select-portal-container/select-portal-container.service';
import type { DropdownOption, DropdownSize } from '../dropdown/dropdown.component';

/**
 * Multi-value select. The value is an array of option `value`s (not option objects),
 * so it can be bound straight to API filter params. Works with `[(value)]` or a form control.
 */
@Component({
  selector: 'joii-multi-select',
  imports: [NgpSelect, NgpSelectDropdown, NgpSelectOption, NgpSelectPortal, InlineButtonComponent, LucideDynamicIcon],
  providers: [provideValueAccessor(MultiSelectComponent)],
  template: `
    <button
      class="dropdown-trigger"
      joii-inline-button
      [fullWidth]="fullWidth()"
      variant="secondary"
      [size]="size()"
      [suffixIcon]="opened() ? chevronUpIcon : chevronDownIcon"
      [active]="opened()"
      ngpSelect
      [ngpSelectMultiple]="true"
      [ngpSelectValue]="value()"
      [ngpSelectDisabled]="disabled() || formDisabled()"
      [ngpSelectDropdownContainer]="selectPortalContainer.currentContainer()"
      (ngpSelectValueChange)="onValueChange($event)"
      (ngpSelectOpenChange)="setOpenState($event)"
      type="button"
    >
      @if (summary(); as text) {
        <span class="select-value">{{ text }}</span>
      } @else {
        <span class="select-placeholder">{{ placeholder() }}</span>
      }
      <ng-template ngpSelectPortal>
        <div ngpSelectDropdown class="joii-dropdown-panel" (click)="$event.stopPropagation()">
          @for (option of options(); track option.value) {
            <button
              [ngpSelectOptionValue]="option.value"
              ngpSelectOption
              joii-inline-button
              variant="secondary"
              [suffixIcon]="isSelected(option) ? checkIcon : undefined"
              [size]="size()"
              [active]="isSelected(option)"
              [fullWidth]="true"
              type="button"
            >
              <span>{{ option.name }}</span>
            </button>
          } @empty {
            <button joii-inline-button class="empty-message" [size]="size()" variant="secondary" [fullWidth]="true">
              No options found
            </button>
          }
        </div>
      </ng-template>
    </button>
  `,
  host: {
    '[style.--joii-dropdown-width]': "width() + 'px'",
    '[attr.data-joii-dropdown-width]': 'width()',
    '[attr.data-full-width]': 'fullWidth()',
  },
  styleUrl: '../dropdown/dropdown.component.css',
})
export class MultiSelectComponent<T = any> implements ControlValueAccessor {
  protected readonly selectPortalContainer = inject(SelectPortalContainerService);

  /** The selectable options. */
  readonly options = input<DropdownOption<T>[]>([]);
  /** Selected option values. Supports two-way binding: `[(value)]`. */
  readonly value = model<T[]>([]);
  readonly placeholder = input<string>('');
  readonly size = input<DropdownSize>('md');
  readonly fullWidth = input<boolean>(false);
  readonly width = input<number>();
  readonly disabled = input<boolean, BooleanInput>(false, { transform: booleanAttribute });

  readonly opened = signal(false);
  protected readonly formDisabled = signal(false);

  /** Trigger label: selected option names joined with commas. */
  readonly summary = computed(() => {
    const selected = this.value() ?? [];
    return this.options()
      .filter((option) => selected.includes(option.value))
      .map((option) => option.name)
      .join(', ');
  });

  readonly chevronDownIcon = LucideChevronDown;
  readonly chevronUpIcon = LucideChevronUp;
  readonly checkIcon = LucideCheck;

  private onChange?: ChangeFn<T[]>;
  protected onTouched?: TouchedFn;

  isSelected(option: DropdownOption<T>): boolean {
    return (this.value() ?? []).includes(option.value);
  }

  onValueChange(values: T[] | T | null | undefined): void {
    const next = Array.isArray(values) ? [...values] : values == null ? [] : [values];
    this.value.set(next);
    this.onChange?.(next);
  }

  setOpenState(isOpen: boolean): void {
    this.opened.set(isOpen);
    if (!isOpen) {
      this.onTouched?.();
    }
  }

  writeValue(value: T[] | null | undefined): void {
    this.value.set(Array.isArray(value) ? [...value] : []);
  }

  registerOnChange(fn: ChangeFn<T[]>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }
}
